import { useCallback, useRef } from "react";
import { useAtomValue, useStore } from "jotai";

import {
  interpreterStoreAtom,
  runtimeApplicationAtom,
  selectedApplicantAtom,
} from "@/store";
import apiCaller from "@/utils/api-caller";
import { parseFormData } from "@/utils/parser";
import { toValidApplicantId } from "@/utils/applicant-id";
import { useCodeHelper } from "./useCodeHelper";
import { needsServerExecution } from "./utils";

const STABLE_SCHEMA = { root: [] as string[], entities: {} };
const STABLE_DATA: Record<string, unknown> = {};

export function useRuntimeReferenceEvaluator() {
  const jotaiStore = useStore();
  const interpreterStore = useAtomValue(interpreterStoreAtom);
  const runtimeApplication = useAtomValue(runtimeApplicationAtom);
  const selectedApplicant = useAtomValue(selectedApplicantAtom);
  const runtimeApplicationRef = useRef(runtimeApplication);
  runtimeApplicationRef.current = runtimeApplication;

  const selectedApplicantRef = useRef(selectedApplicant);
  selectedApplicantRef.current = selectedApplicant;

  const { compileReference } = useCodeHelper({
    formSchema: interpreterStore?.schema ?? STABLE_SCHEMA,
    formData: STABLE_DATA,
    application: runtimeApplication,
  });

  const compileReferenceRef = useRef(compileReference);
  compileReferenceRef.current = compileReference;

  const evaluateReference = useCallback(
    async (code: string): Promise<unknown> => {
      const trimmed = code.trim();
      if (!trimmed) {
        return undefined;
      }

      if (!needsServerExecution(trimmed)) {
        return await compileReferenceRef.current(trimmed);
      }

      const store = jotaiStore.get(interpreterStoreAtom);
      const schema = store?.schema ?? STABLE_SCHEMA;
      const runtimeFormData =
        (runtimeApplicationRef.current?.formInstance?.data as
          | Record<string, unknown>
          | undefined) ?? {};
      const liveValues = (store?.getEntitiesValues?.() ?? {}) as Record<
        string,
        unknown
      >;
      const mergedValues = {
        ...runtimeFormData,
        ...liveValues,
      };
      const formData = parseFormData(mergedValues, schema).data;
      const currentApplicant = jotaiStore.get(selectedApplicantAtom);
      const app = runtimeApplicationRef.current;

      const response = await apiCaller.post("/execution/fetch", {
        function: trimmed,
        formData,
        serialNumber: app?.serialNumber,
        applicantId:
          toValidApplicantId(currentApplicant?.id) ??
          toValidApplicantId(selectedApplicantRef.current?.id) ??
          toValidApplicantId(app?.applicantId) ??
          toValidApplicantId(app?.submittedBy),
      });

      return response.data;
    },
    [jotaiStore],
  );

  return {
    evaluateReference,
  };
}
